import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { z } from 'zod'

import { Loader2Icon } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAppForm } from '@/hooks/form'
import { RtlsArrowRight } from '@/icons'
import $api from '@/services/api'

const fallback = '/' as const

const schema = z.object({
  email: z.string().min(1, 'Email is required').email(),
  password: z.string().min(1, 'Password is required'),
})

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect || fallback })
    }
  },
  component: LoginComponent,
})

function LoginComponent() {
  const auth = useAuth()
  const navigate = useNavigate()
  const login = $api.useMutation('post', '/auth/login')
  const search = Route.useSearch()

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validators: {
      onBlur: schema,
    },
    onSubmit: async ({ value }) => {
      try {
        const response = await login.mutateAsync({ body: value })
        auth.login(response)
        toast.success('Login Successfully', {
          description: 'Welcome back!',
          duration: 3000,
        })
        await navigate({ to: search.redirect || fallback })
      } catch (error) {
        toast.error('Login Failed', {
          description: 'Password or email is incorrect',
          duration: 5000,
        })
      }
    },
  })

  const isSubmitting = login.isPending

  return (
    <div className="min-h-screen overflow-hidden flex flex-col">
      <header className="px-8 py-5 bg-neutral-90">
        <img src="/images/logo.png" alt="Logo" className="h-12 w-auto" />
      </header>
      <div className="h-full flex-1 bg-neutral-10 grid place-items-center px-24">
        <Card className="overflow-hidden p-0 shadow-1! border-[1.5px] border-neutral-10 rounded-2xl w-full max-w-[1240px] h-[668px] mx-auto">
          <CardContent className="grid p-0 md:grid-cols-2 md:gap-6 h-full">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
              className="p-6 md:px-16 md:py-12"
            >
              <div className="flex items-center justify-between mb-16">
                <div className="flex gap-6">
                  <div className="grid place-items-center bg-primary size-9 rounded-full border-[4.5px] shadow-[6px_6px_6px_rgba(0,0,0,0.1)] border-neutral-0">
                    <img
                      src="/images/login/arrow.webp"
                      alt="Login"
                      className="size-[1.125rem]"
                    />
                  </div>
                  <h1 className="text-3xl font-semibold">Login</h1>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="66"
                  height="16"
                  fill="none"
                >
                  <circle cx="8.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="19.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="30.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="41.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="52.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="63.5" cy="2.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="30.5" cy="13.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="41.5" cy="13.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="52.5" cy="13.5" r="2.5" fill="#BFB0DC" />
                  <circle cx="63.5" cy="13.5" r="2.5" fill="#BFB0DC" />
                </svg>
              </div>
              <div className="space-y-8">
                <div className="flex flex-col gap-6">
                  <form.AppField name="email">
                    {(field) => (
                      <field.TextField
                        label="Email"
                        placeholder="Input email"
                        required
                        disabled={isSubmitting}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="password">
                    {(field) => (
                      <div className="flex flex-col gap-1.5">
                        <field.TextField
                          label="Password"
                          placeholder="Input password"
                          required
                          type="password"
                          disabled={isSubmitting}
                        />
                        <Link
                          to="/"
                          className="font-semibold text-primary ml-auto"
                        >
                          Forgot Password?
                        </Link>
                      </div>
                    )}
                  </form.AppField>
                </div>
                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2Icon className="animate-spin" />
                    ) : (
                      <>
                        Login <RtlsArrowRight className="size-3" />
                      </>
                    )}
                  </Button>
                  <div className="text-center">
                    Don't have an account?{' '}
                    <Link to="/" className="font-semibold text-primary">
                      Register
                    </Link>
                  </div>
                </div>
              </div>
            </form>
            <div className="relative hidden md:block">
              <img
                src="/images/login/login-illustration.svg"
                alt="Image"
                className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale rounded-2xl"
              />
            </div>
          </CardContent>
        </Card>
      </div>
      <footer className="text-center py-6 px-8 text-neutral-60 italic font-semibold text-sm">
        @Lateen sheet dead smartly schooner blimey topgallant. Crow's road
        league reef chains across jack yawl coffer.
      </footer>
    </div>
  )
}
